import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { AuroraText } from '@/registry/magicui/aurora-text'

interface ModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  showCloseButton?: boolean
}

export const Modal = ({ open, onClose, children, showCloseButton = false }: ModalProps) => {
  // 🔒 Lock body scroll when modal is open (iOS-safe)
  useEffect(() => {
    if (open) {
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
    }
    return () => document.body.classList.remove('no-scroll')
  }, [open])

  if (!open) return null

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div
        className="
          glass-bordered bg-background rounded-2xl relative
          w-full max-w-[520px]          /* consistent modal width */
          max-h-[88vh]                  /* prevents tall distortion */
          overflow-y-auto
          p-6 sm:p-8
        "
      >
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 z-10"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {children}
      </div>
    </div>
  )

  // Render modal at document root level using Portal
  return createPortal(modalContent, document.body)
}

interface ModalHeaderProps {
  icon?: ReactNode
  stepNumber?: string | number
  title: string
  description?: string
}

export const ModalHeader = ({ icon, stepNumber, title, description }: ModalHeaderProps) => {
  return (
    <div className="col-2 flex items-start gap-4 mb-6">
      {icon && (
        <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 backdrop-blur-sm border border-primary/30 rounded-full flex items-center justify-center">
          <div className="text-primary">{icon}</div>
        </div>
      )}
      <div>
        <h3 className="text-fluid-lg font-bold text-gray-700 dark:text-gray-100">
          {stepNumber ? `Step ${stepNumber}: ` : ''}
          {title}
        </h3>
        {description && <p className="text-fluid-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
  )
}
interface ModalContentProps {
  children: ReactNode
  className?: string
}

export const ModalContent = ({ children, className = '' }: ModalContentProps) => {
  return <div className={`glass-subtle bg-background rounded-xl p-5 ${className}`}>{children}</div>
}

interface ModalFooterProps {
  children: ReactNode
}

export const ModalFooter = ({ children }: ModalFooterProps) => {
  return <div className="mt-4">{children}</div>
}
